import { first } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { usePersistentDeliveryDates } from 'src/dish-first/usePersistentDeliveryDates';
import {
  DishFirstExploreSegmentShefFragment,
  DishFirstFoodItemsSegmentFragment,
} from 'src/gqlReactTypings.generated.d';
import { DishFirstExploreWidthLimit } from 'src/pages/consumer/dish-first/explore/components/SharedStyles';
import { ISegmentMetrics } from 'src/pages/consumer/explore/SegmentAnalytics';
import { ScrollPosition } from 'src/shared/design-system/DynamicImage';
import { useHistory } from 'src/shared/hooks/useHistory';
import { minScreen } from 'src/shared/utils/StyledComponentUtils';
import styled from 'styled-components';
import { Colors, Font, ScreenWidth, Spacing } from 'web-common/src/shared/styles';
import { useFilterQueryParams } from '../../../../../../shared/design-system/DishFirst/DishModal/utils';
import { trackDishCardClicked } from '../../../../../../shared/design-system/DishFirstCard';
import { useTracker } from '../../../../../../shared/hooks/useTracker';
import { getExploreShefMenuUrl } from '../../utils';
import { ExploreShefCard, ExploreShefCardOnClickParams } from './ExploreShefCard';
import { isExploreSegmentShef } from './utils';

const MaxWidthContainer = styled(DishFirstExploreWidthLimit)`
  margin-top: ${Spacing.SINGLE_HALF};
  margin-bottom: ${Spacing.DOUBLE_HALF};
`;

const Header = styled.div`
  padding: ${Spacing.NONE} ${Spacing.SINGLE};
  display: flex;
  flex-direction: row;

  margin-bottom: ${Spacing.SINGLE_HALF};
  cursor: default;
`;
const SegmentHeaderTitle = styled.h1`
  font-family: ${Font.Family.SANS};
  font-weight: ${Font.Weight.BOLD};
  font-size: ${Font.Size.XL};
  color: ${Colors.GRAY_DARK_30};
  margin: unset;

  ${minScreen(ScreenWidth.SMALL)} {
    font-size: ${Font.Size.XXL};
    gap: ${Font.Size.XXL};
  }
`;

const Collection = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${Spacing.SINGLE};
  padding: ${Spacing.NONE} ${Spacing.SINGLE};

  ${minScreen(ScreenWidth.SMALL)} {
    gap: ${Spacing.SINGLE_HALF};
  }

  & > * {
    box-shadow: 10px 16px 30px rgba(0, 0, 0, 0.1);
  }
`;

interface IProps {
  className?: string;
  segment: DishFirstFoodItemsSegmentFragment;
  segmentMetrics: ISegmentMetrics;
  showHeader?: boolean;
  scrollPosition?: ScrollPosition;
}

export const DishFirstShefsCardCollection: React.FC<IProps> = ({
  className,
  segment,
  segmentMetrics,
  showHeader = true,
  scrollPosition,
}): JSX.Element => {
  const [deliveryDates] = usePersistentDeliveryDates();
  const deliveryDate = first(deliveryDates);
  const tracker = useTracker();
  const { appendFiltersQueryStringToUrl, filtersQueryParams } = useFilterQueryParams();

  const history = useHistory();
  const openShefMenu = useCallback(
    ({ exploreSegmentShef, idx, segmentMetrics: metrics }: ExploreShefCardOnClickParams) => {
      trackDishCardClicked(tracker, segmentMetrics, 'shef', filtersQueryParams, idx ?? -1);
      const shefPage = appendFiltersQueryStringToUrl(
        getExploreShefMenuUrl(exploreSegmentShef.shef.id, deliveryDate, metrics, idx)
      );
      history.push(shefPage);
    },
    [history, deliveryDate, appendFiltersQueryStringToUrl, filtersQueryParams, segmentMetrics, tracker]
  );

  const items: DishFirstExploreSegmentShefFragment[] = useMemo(
    () => segment.content.items.filter(isExploreSegmentShef),
    [segment.content.items]
  );

  return (
    <MaxWidthContainer className={className}>
      {showHeader && (
        <Header>
          <SegmentHeaderTitle>{segment.displayName}</SegmentHeaderTitle>
        </Header>
      )}

      <Collection>
        {items?.map((item, idx) => (
          <ExploreShefCard
            key={item.shef.id}
            idx={idx}
            deliveryDate={deliveryDate}
            exploreSegmentShef={item}
            href={appendFiltersQueryStringToUrl(getExploreShefMenuUrl(item.shef.id, deliveryDate, segmentMetrics, idx))}
            onClick={openShefMenu}
            segment={segment}
            segmentMetrics={segmentMetrics}
            location='dish-first-explore-page'
            isResponsive
            scrollPosition={scrollPosition}
          />
        ))}
      </Collection>
    </MaxWidthContainer>
  );
};
